import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Popover from "@radix-ui/react-popover";
import {
  Box,
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { ImageOff, Image as ImageIcon, Sparkles, Upload } from "lucide-react";
import { fileService } from "@/services/breeze/FileService";
import { useDebounce } from "@/hooks/useDebounce";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useRef } from "react";
import {
  StepOutputItems,
  TriggerDataSubmenu,
} from "@/components/workflow/fields/shared/VariableMenu";
import { WorkflowStep } from "@/types/breeze/Workflow";

type TokenGroup = "Project" | "URL" | "Brand" | "Body";

const BUILT_IN_TOKENS: { value: string; label: string; group: TokenGroup }[] = [
  { value: "OrgName", label: "Project name", group: "Project" },
  { value: "OrgId", label: "Project ID", group: "Project" },
  { value: "BaseUrl", label: "Base URL", group: "URL" },
  { value: "FrontendUrl", label: "Frontend URL", group: "URL" },
  { value: "LoginUrl", label: "Login URL", group: "URL" },
  { value: "DashboardUrl", label: "Dashboard URL", group: "URL" },
  { value: "ConfirmationUrl", label: "Confirmation URL", group: "URL" },
  { value: "InviteUrl", label: "Invite URL", group: "URL" },
  { value: "MagicLinkUrl", label: "Magic link URL", group: "URL" },
  { value: "PasswordResetUrl", label: "Password reset URL", group: "URL" },
  { value: "BrandColor", label: "Brand colour", group: "Brand" },
  { value: "BrandColorHover", label: "Brand colour (hover)", group: "Brand" },
  { value: "Title", label: "Title (page heading)", group: "Body" },
  { value: "Content", label: "Content (template body)", group: "Body" },
];

const TOKEN_GROUP_ORDER: TokenGroup[] = ["Project", "Brand", "URL", "Body"];

const TOKEN_PATTERN = /\{\{\s*([A-Za-z_][\w.]*)\s*\}\}/g;
function findCustomTokens(content: string): string[] {
  const seen = new Set<string>();
  const known = new Set(BUILT_IN_TOKENS.map((t) => t.value));
  const re = new RegExp(TOKEN_PATTERN.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (!known.has(m[1])) seen.add(m[1]);
  }
  return Array.from(seen).sort();
}

export interface WorkflowAutoMapContext {
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
  /** Current payload JSON the workflow step holds — read to compute unique token names. */
  payloadJson: string;
  /** Apply a new payload JSON (called after auto-mapping a workflow value to a token). */
  onPayloadChange: (next: string) => void;
}

export interface EmailTemplateToolbarProps {
  /** Current template body — used to surface custom tokens already in use. */
  content: string;
  /** Insertion sink the parent chooses based on the active editor. */
  onInsert: (text: string) => void;
  /** Hide the image picker (eg. for the shell editor where CID-substitution doesn't apply). */
  disableImages?: boolean;
  /** When present, expose a "Insert workflow value" action that auto-maps a
   *  workflow expression to a fresh template token and updates the step payload. */
  workflowContext?: WorkflowAutoMapContext;
}

export default function EmailTemplateToolbar({
  content,
  onInsert,
  disableImages = false,
  workflowContext,
}: EmailTemplateToolbarProps) {
  const customTokens = useMemo(() => findCustomTokens(content), [content]);

  // Step-value insertions auto-map: a fresh token name is generated, inserted
  // into the template at the cursor, and stored in the workflow step's payload
  // pointing at the original expression. The author never sees JSON.
  const handleStepInsert = (workflowExpression: string) => {
    if (!workflowContext) return;
    const inner = workflowExpression.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
    const existing = parsePayload(workflowContext.payloadJson);
    const tokenName = uniqueTokenName(inner, existing);
    existing[tokenName] = workflowExpression;
    workflowContext.onPayloadChange(JSON.stringify(existing, null, 2));
    onInsert(`{{${tokenName}}}`);
  };

  return (
    <Flex gap="2" align="center" mb="2" wrap="wrap">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button type="button" variant="soft" size="1" color="gray">
            <Sparkles size={13} />
            Insert variable
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content style={{ minWidth: 260, zIndex: 11000 }}>
          <VariablePickerItems
            customTokens={customTokens}
            onInsert={onInsert}
            handleStepInsert={handleStepInsert}
            workflowContext={workflowContext}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {!disableImages && (
        <>
          <Box style={{ width: 1, height: 20, background: "var(--gray-5)", margin: "0 4px" }} />
          <InsertImageButton onInsert={onInsert} />
        </>
      )}
    </Flex>
  );
}

interface SubjectVariableButtonProps {
  content: string;
  onInsert: (text: string) => void;
  workflowContext?: WorkflowAutoMapContext;
}

export function SubjectVariableButton({
  content,
  onInsert,
  workflowContext,
}: SubjectVariableButtonProps) {
  const customTokens = useMemo(() => findCustomTokens(content), [content]);

  const handleStepInsert = (workflowExpression: string) => {
    if (!workflowContext) {
      onInsert(workflowExpression);
      return;
    }
    const inner = workflowExpression.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
    const existing = parsePayload(workflowContext.payloadJson);
    const tokenName = uniqueTokenName(inner, existing);
    existing[tokenName] = workflowExpression;
    workflowContext.onPayloadChange(JSON.stringify(existing, null, 2));
    onInsert(`{{${tokenName}}}`);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton type="button" variant="soft" size="2" color="gray" title="Insert variable">
          <Sparkles size={14} />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" style={{ minWidth: 260, zIndex: 11000 }}>
        <VariablePickerItems
          customTokens={customTokens}
          onInsert={onInsert}
          handleStepInsert={handleStepInsert}
          workflowContext={workflowContext}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

interface VariablePickerItemsProps {
  customTokens: string[];
  onInsert: (text: string) => void;
  handleStepInsert: (text: string) => void;
  workflowContext?: WorkflowAutoMapContext;
}

function VariablePickerItems({
  customTokens,
  onInsert,
  handleStepInsert,
  workflowContext,
}: VariablePickerItemsProps) {
  return (
    <>
      {workflowContext && (
        <>
          <DropdownMenu.Label>From this workflow</DropdownMenu.Label>
          <DropdownMenu.Item onSelect={() => handleStepInsert("{{ $anythink.trigger.id }}")}>
            Trigger Job ID
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={() => handleStepInsert("{{ $anythink.trigger.data }}")}>
            Trigger Data (Entire Object)
          </DropdownMenu.Item>
          {workflowContext.triggerEntity ? (
            <TriggerDataSubmenu
              entityName={workflowContext.triggerEntity}
              onInsert={handleStepInsert}
            />
          ) : (
            <DropdownMenu.Item onSelect={() => handleStepInsert("{{ $anythink.trigger.data. }}")}>
              Trigger Data Property…
            </DropdownMenu.Item>
          )}
          {workflowContext.existingSteps.length > 0 && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Label>Preceding Steps Output</DropdownMenu.Label>
              <StepOutputItems
                steps={workflowContext.existingSteps}
                onInsert={handleStepInsert}
              />
            </>
          )}
          <DropdownMenu.Separator />
        </>
      )}

      <DropdownMenu.Label>Project & branding</DropdownMenu.Label>
      {TOKEN_GROUP_ORDER.map((group) => {
        const items = BUILT_IN_TOKENS.filter((t) => t.group === group);
        return (
          <DropdownMenu.Sub key={group}>
            <DropdownMenu.SubTrigger>{group}</DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent style={{ minWidth: 220, zIndex: 11000 }}>
              {items.map((t) => (
                <DropdownMenu.Item key={t.value} onSelect={() => onInsert(`{{${t.value}}}`)}>
                  <Flex justify="between" align="center" gap="3" style={{ width: "100%" }}>
                    <Text size="2">{t.label}</Text>
                    <Text size="1" color="gray">{`{{${t.value}}}`}</Text>
                  </Flex>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        );
      })}
      {customTokens.length > 0 && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Label>From this template</DropdownMenu.Label>
          {customTokens.map((t) => (
            <DropdownMenu.Item key={t} onSelect={() => onInsert(`{{${t}}}`)}>
              <Text size="2">{`{{${t}}}`}</Text>
            </DropdownMenu.Item>
          ))}
        </>
      )}
      <DropdownMenu.Separator />
      <DropdownMenu.Item onSelect={() => onInsert("{{NewVariable}}")}>
        <Text size="2">+ Add custom variable</Text>
      </DropdownMenu.Item>
    </>
  );
}


function parsePayload(json: string): Record<string, string> {
  try {
    const parsed = JSON.parse(json || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        out[k] = typeof v === "string" ? v : JSON.stringify(v);
      }
      return out;
    }
  } catch {
    // ignore — payload may be partially edited
  }
  return {};
}

function uniqueTokenName(innerExpr: string, existing: Record<string, string>): string {
  const base = tokenBaseFromExpression(innerExpr);
  // If this exact expression already maps to a token, reuse it.
  for (const [k, v] of Object.entries(existing)) {
    if (v.replace(/\s+/g, "") === `{{${innerExpr.replace(/\s+/g, "")}}}`) return k;
  }
  if (!existing[base]) return base;
  for (let i = 2; i < 100; i++) {
    const candidate = `${base}${i}`;
    if (!existing[candidate]) return candidate;
  }
  return `${base}${Date.now()}`;
}

function tokenBaseFromExpression(inner: string): string {
  // inner: "$anythink.trigger.data.user.email" → "UserEmail"
  //        "$anythink.steps.upload.data.id"   → "UploadId"
  //        "$anythink.now"                    → "Now"
  let cleaned = inner
    .replace(/^\$anythink\./, "")
    .replace(/^trigger\.data\./, "")
    .replace(/^trigger\./, "")
    .replace(/^steps\.([^.]+)\.data\./, (_m, step) => `${step}.`)
    .replace(/^steps\.([^.]+)\./, (_m, step) => `${step}.`);
  const parts = cleaned.split(".").filter(Boolean);
  const useParts = parts.length > 2 ? parts.slice(-2) : parts;
  return useParts
    .map((p) => p.replace(/_(\w)/g, (_m, c) => c.toUpperCase()))
    .map((p) => (p[0] ? p[0].toUpperCase() + p.slice(1) : p))
    .join("") || "Value";
}

function PickerRow({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <Flex
      align="center"
      gap="3"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        padding: "8px 10px",
        borderBottom: "1px solid var(--gray-3)",
        cursor: "pointer",
        background: hover ? "var(--accent-3)" : "transparent",
        transition: "background-color 80ms ease",
      }}
    >
      {children}
    </Flex>
  );
}

export function FileThumbnail({ fileId, alt }: { fileId: number; alt: string }) {
  const { data: url } = useQuery({
    queryKey: ["file-signed-url", fileId],
    queryFn: () => fileService.getFileSignedUrl(fileId),
    staleTime: 4 * 60 * 1000,
  });
  const [errored, setErrored] = useState(false);
  return (
    <Box style={{
      width: 56, height: 56, flexShrink: 0,
      borderRadius: 6, overflow: "hidden",
      background: "var(--gray-3)",
      display: "flex", alignItems: "center", justifyContent: "center",
      border: "1px solid var(--gray-4)",
    }}>
      {url && !errored ? (
        <img
          src={url}
          alt={alt}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <ImageOff size={18} color="var(--gray-8)" />
      )}
    </Box>
  );
}

function prettyBytes(n: number | undefined) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function InsertImageButton({ onInsert }: { onInsert: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const debounced = useDebounce(search, 250);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["any-files-image-picker", debounced],
    queryFn: () => fileService.getAll(1, 25, undefined, "asc", debounced || undefined, "image"),
    enabled: open,
  });
  const items: any[] = (data as any)?.items ?? (Array.isArray(data) ? data : []);

  const handleUpload = async (file: globalThis.File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploaded = await fileService.create(formData);
      const safeName = (uploaded.original_file_name || `file-${uploaded.id}`)
        .replace(/[^A-Za-z0-9._-]/g, "_");
      const cid = `${uploaded.id}-${safeName}`;
      const altSafe = safeName.replace(/"/g, "&quot;");
      const tag = `<img src="cid:${cid}" data-anythink-file-id="${uploaded.id}" alt="${altSafe}" style="max-width:100%; height:auto;" />`;
      queryClient.invalidateQueries({ queryKey: ["any-files-image-picker"] });
      onInsert(tag);
      setOpen(false);
    } catch (err) {
      toast.error("Failed to upload image");
      // eslint-disable-next-line no-console
      console.warn("Image upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button type="button" variant="soft" size="1" color="gray">
          <ImageIcon size={13} />
          Insert image
        </Button>
      </Popover.Trigger>
      <Popover.Content
        sideOffset={4}
        align="start"
        style={{
          background: "var(--color-panel-solid)",
          border: "1px solid var(--gray-6)",
          borderRadius: 6,
          width: 360,
          boxShadow: "var(--shadow-3)",
          zIndex: 11000,
        }}
      >
        <Box p="2">
          <Flex gap="2" align="center" mb="2">
            <Box style={{ flex: 1 }}>
              <TextField.Root
                size="1"
                placeholder="Search image files…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="1"
              variant="soft"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Upload a new image"
            >
              <Upload size={13} />
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </Flex>
          <Box style={{
            maxHeight: 320, overflowY: "auto",
            border: "1px solid var(--gray-4)", borderRadius: 4,
          }}>
            {isLoading ? (
              <Box p="3"><Text size="1" color="gray">Searching…</Text></Box>
            ) : items.length === 0 ? (
              <Box p="3"><Text size="1" color="gray">No images.</Text></Box>
            ) : (
              items.map((f: any) => {
                const name = f.original_file_name || `file-${f.id}`;
                const cid = `${f.id}-${name.replace(/[^A-Za-z0-9._-]/g, "_")}`;
                const altSafe = name.replace(/"/g, "&quot;");
                const tag = `<img src="cid:${cid}" data-anythink-file-id="${f.id}" alt="${altSafe}" style="max-width:100%; height:auto;" />`;
                return (
                  <PickerRow
                    key={f.id}
                    onClick={() => { onInsert(tag); setOpen(false); }}
                  >
                    <FileThumbnail fileId={f.id} alt={name} />
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="2" weight="medium" style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>{name}</Text>
                      <Text size="1" color="gray">
                        {f.file_type}
                        {f.file_size ? ` · ${prettyBytes(f.file_size)}` : ""}
                      </Text>
                    </Box>
                  </PickerRow>
                );
              })
            )}
          </Box>
          <Text size="1" color="gray" mt="2" style={{ display: "block" }}>
            Image renders inline in the sent email via CID, and uses a signed URL in
            the preview here. The file stays tenant-scoped.
          </Text>
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}
